import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { _, TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { Button } from 'primeng/button';
import { SvgIconComponent } from 'angular-svg-icon';
import { DayJSDateFormat } from '@app/core/enums/date-format.enum';
import { DayjsPipe } from '@app/shared/pipes/dayjs/dayjs.pipe';
import { FormattingDataService } from '@app/core/services/formatting-data/formatting-data.service';
import { IconBadgeComponent } from '@app/shared/components/icon-badge/icon-badge.component';
import { pressureUnitLocalize } from '@app/shared/units/pressure/pressure-unit.localize';
import { flowUnitLocalize } from '@app/shared/units/flow/flow-unit.localize';
import { LdEventRepairStatus } from '@app/main/leak-detection/enums/ld-event-repair-status.enum';
import {
  ldEventInvestigationResultIcon,
  ldEventRepairStatusIcon,
} from '@app/main/leak-detection/configs/ld-event-status.config';
import {
  dialogEventRepairBorderTheme,
  dialogEventResultBadgeTheme,
  dialogEventResultLabel,
  DIALOG_EVENT_NEUTRAL_BADGE_BORDER,
  DIALOG_EVENT_NEUTRAL_BADGE_THEME,
} from '@app/main/leak-detection/dialogs/dialog-event/configs/dialog-event-badge.config';
import {
  nonLeakNoiseSourceLocalize,
  soilCompositionLocalize,
} from '@app/main/leak-detection/dialogs/dialog-event/localize/dialog-event-fields';
import { DialogEventState } from '@app/main/leak-detection/dialogs/dialog-event/services/dialog-event-state.service';
import { DialogEventTabsService } from '@app/main/leak-detection/dialogs/dialog-event/services/dialog-event-tabs.service';
import { DialogEventEditor } from '@app/main/leak-detection/dialogs/dialog-event/enums/dialog-event-editor.enum';
import { DIALOG_EVENT_MEASURE_DECIMALS } from '@app/main/leak-detection/dialogs/dialog-event/configs/dialog-event-format.config';
import {
  leakSizeDisplayValue,
  pressureDisplayValue,
} from '@app/main/leak-detection/dialogs/dialog-event/utils/dialog-event-draft';

@Component({
  selector: 'gc-dialog-event-investigation-result',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslateDirective,
    TranslatePipe,
    Button,
    SvgIconComponent,
    DayjsPipe,
    IconBadgeComponent,
  ],
  templateUrl: './dialog-event-investigation-result.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex flex-col min-h-0' },
})
export class DialogEventInvestigationResultComponent {
  protected readonly dialogState = inject(DialogEventState);
  private readonly tabs = inject(DialogEventTabsService);
  private readonly formatting = inject(FormattingDataService);

  protected readonly dateFormat = DayJSDateFormat.DDMMYYYY;

  protected readonly noData = this.formatting.NO_DATA_VALUE_PLACEHOLDER_KEY;

  protected readonly draft = this.dialogState.draft;

  protected readonly hasResult = computed(() => !!this.dialogState.investigationResult());

  protected readonly resultIcon = computed(() => {
    const result = this.dialogState.investigationResult();

    return result ? ldEventInvestigationResultIcon[result] : null;
  });

  protected readonly resultLabel = computed(() => {
    const result = this.dialogState.investigationResult();

    return result ? dialogEventResultLabel(result, this.dialogState.repairStatus()) : '';
  });

  protected readonly repairOverlayIcon = computed(() => {
    const repairStatus = this.dialogState.repairStatus();

    return repairStatus ? ldEventRepairStatusIcon[repairStatus] : undefined;
  });

  protected readonly panelTheme = computed(() => {
    const repairStatus = this.dialogState.repairStatus();

    return repairStatus ? dialogEventResultBadgeTheme[repairStatus] : DIALOG_EVENT_NEUTRAL_BADGE_THEME;
  });

  protected readonly panelBorder = computed(() => {
    const repairStatus = this.dialogState.repairStatus();

    return repairStatus ? dialogEventRepairBorderTheme[repairStatus] : DIALOG_EVENT_NEUTRAL_BADGE_BORDER;
  });

  protected readonly pressureDisplay = computed(() => this.formatMeasure(pressureDisplayValue(this.draft())));

  /** the unit goes only with a value — an empty measure shows the placeholder alone, not `- bar` */
  protected readonly pressureUnitLabel = computed(() => {
    const { pressure, pressureUnit } = this.draft();

    return pressure === null ? null : pressureUnitLocalize[pressureUnit];
  });

  protected readonly leakSizeDisplay = computed(() => this.formatMeasure(leakSizeDisplayValue(this.draft())));

  protected readonly leakSizeUnitLabel = computed(() => {
    const { leakSize, leakSizeUnit } = this.draft();

    return leakSize === null ? null : flowUnitLocalize[leakSizeUnit];
  });

  protected readonly soilLabel = computed(() => {
    const soil = this.draft().soilComposition;

    return soil ? soilCompositionLocalize[soil] : '';
  });

  protected readonly repairDateLabel = computed(() => {
    if (!this.draft().repairDate) return null;

    switch (this.dialogState.repairStatus()) {
      case LdEventRepairStatus.RepairScheduled:
        return _(`@@fp-event-result__scheduled-repair-date`);
      case LdEventRepairStatus.Repaired:
        return _(`@@fp-event-result__repair-date`);
      default:
        return null;
    }
  });

  protected readonly nonLeakLabel = computed(() => {
    const source = this.draft().nonLeakNoiseSource;

    return source ? nonLeakNoiseSourceLocalize[source] : '';
  });

  protected editResult(): void {
    this.tabs.openEditor(DialogEventEditor.Result);
  }

  private formatMeasure(value: number | null): string {
    return this.formatting.formatNumber(value, 0, DIALOG_EVENT_MEASURE_DECIMALS);
  }
}
